import { Injectable } from '@angular/core';
import { environment } from "../../environments/environment";
import { PromotionModel } from "../models/promotion.model";
import { Subject } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  /* This service acts as a proxy for the environment file to centralize configuration. */

  public apiFlipped$ = new Subject<{ token: string, merchant_id: number }>();

  // --- Core Identification & Paths ---
  readonly name = environment.rivalName; //shortName used for assets(ex.landings) and rival API URLs => for settings
  casinoId = environment.casinoId; //merchant_id on GP brands => for Http calls
  isRivalLook: boolean = environment.platform !== 'gamingpodium'; //in sidebar, sidebar-structure component for design
  isRivalCasino: boolean;
  /* used as: platform related settings
  - navigate to notFound in legacy/reset-forgot-pw-in-lobby components
  - GP tracking after login + transaction watching
  - fixing tracker on some GP landings
  - error handling in security component
  - categories to show on home (displayedCategories)
  - using iframe urls (no extra info needed on GP)
  - chatwoot service => sending data based on original platform
  */
  readonly assetsPath: string; //used for branded assets in components (because rivalName can be different from our brandName)

  // --- API & CDN Endpoints ---
  apiUrl: string;
  cdnUrl: string;

  // --- Data ---
  readonly publicPromotions: PromotionModel[] = environment.publicPromotions;

  // --- Branding & SEO ---
  readonly brandName = environment.prettyName; //the casino name as we use it
  readonly GA_TRACKING = environment.GA_TRACKING;
  readonly logoName = environment.logoName;
  readonly logoAltText = environment.logoAltText;
  readonly metaDesc = environment.metaDescription;

  // --- Support & Communication & Chat ---
  readonly contactEmail = environment.contactEmail;
  readonly docsEmail = environment.docsEmail;

  chatSystem: string = environment.chatSystem;
  readonly rivalChatGroupName: string | undefined;
  readonly chatwoot_brandWebsiteToken: string | undefined = environment.chatwoot_websiteToken;
  readonly chatwoot_userIdentifier: string | undefined = environment.chatwoot_userIdentifier;
  // readonly backoffice_link: string | undefined = environment.backoffice_url; //for chatwoot
  backoffice_link: string | undefined //for chatwoot - we dont need rival bo anymore

  // --- Legal & License ---
  readonly licenceNo = environment.licenseNo;
  readonly licenseInfo: { pdb_name: string, pdb_address: string, lh_name: string, lh_address: string } | undefined = environment.license;
  readonly withdrawalTime = environment.legalData.withdrawalTime;
  readonly minDepositAmount = environment.legalData.minDepositAmount;
  readonly isAffTermsNeeded = environment.legalData.affTerms;
  readonly minAgeAtReg = environment.minAgeAtReg ?? 18;

  // --- Features & Flags ---
  readonly hasComplaintsForm = environment.hasComplaintsForm;
  readonly hasMinimalRegistration = environment.hasMinReg;
  readonly hasOnlyLandings = environment.isLandingsOnly; //navigate from landings + extra languages(dela + slotvibe)
  readonly landingLinks: { home?: string, casino?: string, sport?: string } = environment.ctaLinks;


  constructor() {
    // 1. Core Identification
    this.isRivalCasino = environment.platform === 'rival';
    this.assetsPath = environment.assetsPath ?? `assets/brands/${this.name}`;

    // 2. Endpoints
    this.apiUrl = environment.apiUrl ?? `https://www.casinocontroller.com/${this.name}/api-player/v3/jsonrpc`;
    this.cdnUrl = `${environment.cdnUrl}/${environment.platform}.json`;
    this.backoffice_link = environment.backoffice_url;

    // 3. Chat Logic
    this.rivalChatGroupName = this.isRivalCasino ? environment.rivalChatGroupName : undefined; //we dont care if its not rival casino

    //check if this is a flippedAPI on hard refresh
    try {
      const flippedMerchantId = localStorage.getItem('flippedAPI');
      if (flippedMerchantId) {
        this.casinoId = +flippedMerchantId;
        this.apiUrl = 'https://gp.0x6e.com';
        this.cdnUrl = 'https://cdn.0x6e.com/platforms/gamingpodium.json';
        this.backoffice_link = 'https://gp-bo.com';

        this.isRivalCasino = false;
        this.chatSystem = this.chatwoot_brandWebsiteToken ? 'chatwoot' : 'other';
      }
    } catch (err) {
      console.warn('LocalStorage error in ConfigService', err);
    }
  }

  flipAPI(data: { token: string, merchant_id: number }) {
    // console.log('flipping API to GP:', data);
    this.apiUrl = 'https://gp.0x6e.com';
    this.cdnUrl = 'https://cdn.0x6e.com/platforms/gamingpodium.json';
    this.backoffice_link = 'https://gp-bo.com';

    this.isRivalCasino = false;

    this.chatSystem = this.chatwoot_brandWebsiteToken ? 'chatwoot' : 'other';

    this.casinoId = data.merchant_id;
    //save merchant ID in LS => so we can store flipped state
    try {
      localStorage.setItem('flippedAPI', data.merchant_id.toString());
    } catch (err) {
      console.warn('LocalStorage error while flipping API', err);
    }

    //make a BS onConfigChange => subscribe to that in authService
    this.apiFlipped$.next(data);
  }

  unflipAPI() {
    //todo flip back api on logout -> reload frech page?
    //currently not used - cant test it as there is no flipAPI currently for my test users
    window.location.reload();
  }
}

